import { useClientPortalSubmit, useFormikSubmit } from "@certn/api";
import { useClientPortalAPI } from "@certn/api/src/useClientPortalAPI";
import { showToast } from "@certn/cascadia";
import {
  BasicButton,
  Card,
  Container,
  Divider,
  Link,
  PrimaryButton,
  SectionMessage,
  SelectFormikField,
  SelectItem,
  Stack,
  Text,
  TextInputFormikField,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { Form, Formik, FormikHelpers } from "formik";
import { useCallback, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { object, string } from "yup";

import { APIError } from "@certn/api";
import { components } from "@certn/api/generated-schemas/client-portal";
import {
  PageUpIntegrationContext,
  AtsIntegration,
} from "../../PageUpIntegrationContext";
import { getDefaultDatacenter, renderDatacenterOptions } from "../../utils";
import { AtsIntegrationStepLayout } from "../AtsIntegrationStepLayout";
import { WhatsThis } from "../../../components/WhatsThis";

interface Props {
  initialValues?: components["schemas"]["AtsIntegrationDetail"];
  reload?: () => void;
}

export function PageUpSetupStep({ initialValues, reload }: Props = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [authenticationError, setAuthenticationError] = useState(false);
  const [isSystemError, setIsSystemError] = useState(false);
  const context = useContext(PageUpIntegrationContext);
  const { atsIntegrationData: contextData, setAtsIntegration } = context || {
    atsIntegrationData: null,
    setAtsIntegration: () => {},
  };

  // Determine if we're in edit mode
  const isEditMode = !!initialValues?.id;

  // Use initialValues for edit mode, context for create mode
  const integrationData = isEditMode
    ? {
        client_id: initialValues?.pageup_integration?.client_id || "",
        client_secret: initialValues?.pageup_integration?.client_secret || "",
        environment: (initialValues?.pageup_integration?.environment ||
          "UAT") as "LIVE" | "UAT",
        datacenter_id: (initialValues?.pageup_integration?.datacenter_id ||
          getDefaultDatacenter()) as components["schemas"]["DatacenterIdEnum"],
        default_group_id:
          initialValues?.pageup_integration?.default_group_id || "",
        report_url_type: (initialValues?.report_url_type ||
          "CLIENT_PORTAL_URL") as components["schemas"]["ReportUrlTypeEnum"],
      }
    : contextData || {
        client_id: "",
        client_secret: "",
        environment: "UAT" as "LIVE" | "UAT",
        datacenter_id: getDefaultDatacenter(),
        default_group_id: "",
        report_url_type: "CLIENT_PORTAL_URL",
      };

  const defaultDatacenter = getDefaultDatacenter();

  const { data: customerGroups, isLoading: isLoadingCustomerGroups } =
    useClientPortalAPI(
      "/api/internal/client-portal/ats-integrations/customer-groups/"
    );

  const validationSchema = object().shape({
    client_id: string().required().label(t("Client ID")),
    client_secret: string().required().label(t("Client secret")),
    environment: string().required().label(t("Environment")),
    datacenter_id: string().required().label(t("Datacenter")),
    default_group_id: string().required().label(t("Default group")),
  });

  const { submit: validateAuthRaw, isSubmitting: isValidating } =
    useClientPortalSubmit(
      "/api/internal/client-portal/ats-integrations/pageup/validate-auth/" as any,
      {
        throwErrors: true,
        onFailure: (error: unknown) => {
          // Check if it's a system error (network, timeout, etc.) vs invalid credentials
          const status =
            error instanceof APIError
              ? error.status
              : (error as { status?: number; response?: { status?: number } })
                  ?.status ||
                (error as { status?: number; response?: { status?: number } })
                  ?.response?.status;
          if (status === 400 || status === 401 || status === 403) {
            // Invalid credentials
            setAuthenticationError(true);
            setIsSystemError(false);
          } else {
            // System error
            setIsSystemError(true);
            setAuthenticationError(false);
          }
        },
        onSuccess: () => {
          // Clear any previous errors on successful auth validation
          setAuthenticationError(false);
          setIsSystemError(false);
        },
      }
    );

  const validateAuth = useCallback(
    (
      body: components["schemas"]["PageUpAuthValidation"]
    ): ReturnType<typeof validateAuthRaw> => {
      return validateAuthRaw(
        body as unknown as Parameters<typeof validateAuthRaw>[0]
      );
    },
    [validateAuthRaw]
  );

  const { submit: createIntegration, isSubmitting: isCreating } =
    useFormikSubmit("/api/internal/client-portal/ats-integrations/create/", {
      onSuccess: () => {
        showToast(t("Success, PageUp has been successfully enabled."), {
          type: "success",
        });
        navigate("/settings/ats-integrations");
      },
      showToastOnError: true,
    });

  const { submit: updateIntegration, isSubmitting: isUpdating } =
    useFormikSubmit<components["schemas"]["AtsIntegrationDetail"]>(
      `/api/internal/client-portal/ats-integrations/${initialValues?.id}/update/`,
      {
        onSuccess: () => {
          showToast(t("Integration updated successfully"), {
            type: "success",
          });
          reload?.();
          navigate("/settings/ats-integrations");
        },
        showToastOnError: true,
      }
    );

  const handleSubmit = useCallback(
    async (
      values: AtsIntegration,
      formikHelpers: FormikHelpers<AtsIntegration>
    ) => {
      setAuthenticationError(false);
      setIsSystemError(false);
      if (!isEditMode) {
        setAtsIntegration(values);
      }

      // First validate authentication
      try {
        await validateAuth({
          client_id: values.client_id,
          client_secret: values.client_secret,
          environment:
            values.environment as components["schemas"]["EnvironmentEnum"],
          datacenter_id:
            values.datacenter_id as components["schemas"]["DatacenterIdEnum"],
        });

        // If validation succeeds, create or update the integration
        if (isEditMode) {
          // Update mode - submit all values directly like other integrations
          await updateIntegration(
            {
              report_url_type: (values.report_url_type ||
                "CLIENT_PORTAL_URL") as components["schemas"]["ReportUrlTypeEnum"],
              lever_integration: null,
              workday_integration: null,
              unified_integration: null,
              successfactors_integration: null,
              pageup_integration: {
                client_id: values.client_id,
                client_secret: values.client_secret,
                environment:
                  values.environment as components["schemas"]["EnvironmentEnum"],
                datacenter_id:
                  values.datacenter_id as components["schemas"]["DatacenterIdEnum"],
                default_group_id: values.default_group_id || "",
              },
            } as unknown as components["schemas"]["AtsIntegrationDetail"],
            formikHelpers
          );
        } else {
          // Create mode
          await createIntegration(
            {
              report_url_type: (values.report_url_type ||
                "CLIENT_PORTAL_URL") as components["schemas"]["ReportUrlTypeEnum"],
              pageup_integration: {
                client_id: values.client_id,
                client_secret: values.client_secret,
                environment:
                  values.environment as components["schemas"]["EnvironmentEnum"],
                datacenter_id:
                  values.datacenter_id as components["schemas"]["DatacenterIdEnum"],
                default_group_id: values.default_group_id || "",
              },
            } as components["schemas"]["AtsIntegrationCreate"],
            formikHelpers
          );
        }
      } catch (error: unknown) {
        // Error handling is done in the onFailure callbacks
        // Check if it's a validation error or system error
        const status =
          error instanceof APIError
            ? error.status
            : (error as { status?: number; response?: { status?: number } })
                ?.status ||
              (error as { status?: number; response?: { status?: number } })
                ?.response?.status;
        if (status === 400 || status === 401 || status === 403) {
          setAuthenticationError(true);
          setIsSystemError(false);
        } else if (status !== undefined) {
          setIsSystemError(true);
          setAuthenticationError(false);
        }
      }
    },
    [
      isEditMode,
      setAtsIntegration,
      validateAuth,
      createIntegration,
      updateIntegration,
    ]
  );

  const isSubmitting = isValidating || isCreating || isUpdating;

  return (
    <>
      <Formik
        initialValues={integrationData}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        enableReinitialize={isEditMode}
      >
        {({ handleSubmit, isValid, values, ...helpers }) => {
          const formFields = (
            <>
              {authenticationError && (
                <SectionMessage
                  type="danger"
                  sectionTitle={t("Authentication failed")}
                  icon="error"
                >
                  <Trans>
                    We could not verify your API credentials. Please check your
                    details and try again. If you still encounter an error,
                    please contact your page up administrator.
                  </Trans>
                </SectionMessage>
              )}

              {isSystemError && (
                <SectionMessage
                  type="danger"
                  sectionTitle={t("System error")}
                  icon="error"
                >
                  <Trans>
                    We are not able to verify your API credentials, as the
                    PageUp platform is not responsive. Please try after some
                    time. If the issue persists, please contact Certn support
                    team.
                  </Trans>
                </SectionMessage>
              )}

              <Text style="global/heading-medium">
                {t("PageUp account authentication details")}
              </Text>

              <section>
                <TextInputFormikField
                  name="client_id"
                  label={t("Client ID")}
                  testID="pageup-client-id"
                  info={
                    <WhatsThis>
                      {t(
                        "Your PageUp API key allows Certn to seamlessly integrate with your PageUp account. Please request these from your PageUp representative."
                      )}
                    </WhatsThis>
                  }
                  isRequired
                  isDisabled={isSubmitting}
                />
              </section>

              <section>
                <TextInputFormikField
                  name="client_secret"
                  label={t("Client secret")}
                  testID="pageup-client-secret"
                  type="password"
                  info={
                    <WhatsThis>
                      {t(
                        "This is the Client Secret from PageUp. Please request these from your PageUp representative."
                      )}
                    </WhatsThis>
                  }
                  isRequired
                  isDisabled={isSubmitting}
                />
              </section>

              <section>
                <SelectFormikField
                  label={t("Region")}
                  testID="pageup-datacenter-id"
                  name="datacenter_id"
                  isRequired
                  isDisabled={isSubmitting}
                  defaultValue={defaultDatacenter}
                  info={
                    <WhatsThis>
                      {t(
                        "The region where your data is stored, typically matching your location. PageUp provides this as a datacenter ID (e.g., dc2). If unsure, try your current region."
                      )}
                    </WhatsThis>
                  }
                >
                  {renderDatacenterOptions()}
                </SelectFormikField>
              </section>

              <section>
                <SelectFormikField
                  label={t("Environment")}
                  testID="pageup-environment"
                  name="environment"
                  isRequired
                  isDisabled={isSubmitting}
                  defaultValue="LIVE"
                >
                  <SelectItem id="UAT" textValue={t("UAT")}>
                    {t("UAT")}
                  </SelectItem>
                  <SelectItem id="LIVE" textValue={t("LIVE")}>
                    {t("LIVE")}
                  </SelectItem>
                </SelectFormikField>
              </section>

              <Divider />

              <section>
                <SelectFormikField
                  label={t("Select a default group")}
                  testID="pageup-default-group"
                  name="default_group_id"
                  isRequired
                  isLoading={isLoadingCustomerGroups}
                  isDisabled={isSubmitting}
                >
                  {customerGroups?.map(({ id, name }) => (
                    <SelectItem key={id} id={id} textValue={name}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectFormikField>
              </section>
            </>
          );

          const formContent = (
            <Container
              layout="narrow"
              testID="pageup-setup-page-outermost-container"
              className={styles.container}
            >
              <Form>
                {isEditMode && (
                  <Card type="large" className={styles.card}>
                    <Stack gap="space-4">
                      <div className={styles.mainContentContainer}>
                        {formFields}
                        <Stack
                          orientation="horizontal"
                          justify="end"
                          gap="space-4"
                        >
                          <BasicButton
                            testID="cancel-pageup"
                            type="button"
                            isDisabled={isSubmitting}
                            onPress={() =>
                              navigate("/settings/ats-integrations")
                            }
                          >
                            {t("Cancel")}
                          </BasicButton>
                          <PrimaryButton
                            testID="save-pageup"
                            type="submit"
                            isDisabled={
                              isSubmitting ||
                              !isValid ||
                              !initialValues?.is_active
                            }
                          >
                            {t("Save changes")}
                          </PrimaryButton>
                        </Stack>
                      </div>
                    </Stack>
                  </Card>
                )}

                {!isEditMode && (
                  <>
                    <Stack gap="space-2">
                      <Text
                        as="h1"
                        className={styles.titleSpacing}
                        style="global/title-small"
                      >
                        {t("PageUp Integration")}
                      </Text>
                      <Stack gap="space-2">
                        <Text style="global/heading-medium">
                          {t(
                            "Add your integration details here to setup the connection with Certn. These enable Certn to send information to PageUp."
                          )}
                        </Text>

                        <SectionMessage type="info" icon="info">
                          <Trans>
                            For detailed instructions on setting up your PageUp
                            integration, see our PageUp{" "}
                            <Link
                              textColor="text-weak"
                              testID="deployment-guide"
                            >
                              Deployment Guide
                            </Link>
                            .
                          </Trans>
                        </SectionMessage>
                      </Stack>
                    </Stack>
                    <div className={styles.mainContentContainer}>
                      {formFields}
                    </div>
                  </>
                )}
              </Form>
            </Container>
          );

          if (isEditMode) {
            return formContent;
          }

          return (
            <AtsIntegrationStepLayout
              onComplete={handleSubmit}
              disableCompletion={!isValid}
              submitting={isSubmitting}
            >
              {formContent}
            </AtsIntegrationStepLayout>
          );
        }}
      </Formik>
    </>
  );
}

const styles = {
  titleSpacing: css({ marginBottom: "var(--space-1)" }),
  mainContentContainer: css({
    display: "flex",
    gap: "var(--space-5)",
    flexDirection: "column",
    marginTop: "var(--space-4)",
  }),
  container: css({
    rowGap: "1rem !important",
  }),
  card: css({
    marginTop: "var(--space-4)",
    padding: "var(--space-4)",
    gap: "var(--space-4)",
  }),
};
